package com.patronat.top.client.presentation.profile

import androidx.lifecycle.viewModelScope
import com.patronat.top.client.domain.profile.DeleteClientAccountUseCase
import com.patronat.top.client.domain.profile.GetClientProfileUseCase
import com.patronat.top.client.domain.profile.SubmitClientContactMessageUseCase
import com.patronat.top.client.domain.profile.UpdateClientProfileUseCase
import com.patronat.top.client.domain.reviews.GetClientMyReviewsCommand
import com.patronat.top.client.domain.reviews.GetClientMyReviewsUseCase
import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.models.DomainResult
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.ContactSupportRequest
import com.patronat.top.dto.DeleteCurrentUserRequest
import com.patronat.top.dto.ImageType
import com.patronat.top.dto.UpdateCurrentUserRequest
import com.patronat.top.library.media.domain.command.UploadImageCommand
import com.patronat.top.library.media.domain.usecase.UploadImageUseCase
import kotlinx.coroutines.launch
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_profile_avatar_upload_error
import top.clientapp.generated.resources.client_profile_contact_error
import top.clientapp.generated.resources.client_profile_contact_success
import top.clientapp.generated.resources.client_profile_delete_error
import top.clientapp.generated.resources.client_profile_delete_success
import top.clientapp.generated.resources.client_profile_load_error
import top.clientapp.generated.resources.client_profile_reviews_load_error
import top.clientapp.generated.resources.client_profile_save_error
import top.clientapp.generated.resources.client_profile_save_success
import top.clientapp.generated.resources.client_profile_success_title
import top.clientapp.generated.resources.client_profile_validation_contact_description
import top.clientapp.generated.resources.client_profile_validation_contact_subject
import top.clientapp.generated.resources.client_profile_validation_delete_email
import top.clientapp.generated.resources.client_profile_validation_delete_reason
import top.clientapp.generated.resources.client_profile_validation_first_name
import top.clientapp.generated.resources.client_profile_validation_last_name
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ClientProfileViewModel(
    private val alertController: AlertController,
    private val getProfileUseCase: GetClientProfileUseCase,
    private val updateProfileUseCase: UpdateClientProfileUseCase,
    private val getMyReviewsUseCase: GetClientMyReviewsUseCase,
    private val submitContactMessageUseCase: SubmitClientContactMessageUseCase,
    private val deleteClientAccountUseCase: DeleteClientAccountUseCase,
    private val uploadImageUseCase: UploadImageUseCase,
    private val tokenStorage: TokenStorage
) : BaseViewModel<ClientProfileViewState, ClientProfileEvent, ClientProfileEffect>(
    initialState = ClientProfileViewState()
) {

    override fun onEvent(event: ClientProfileEvent) {
        when (event) {
            ClientProfileEvent.OnRefresh -> loadProfile()
            ClientProfileEvent.OnSignInClicked -> sendEffect(ClientProfileEffect.OpenAuth)
            ClientProfileEvent.OnLogoutClicked -> logout()
            ClientProfileEvent.OnBackClicked -> updateState { it.copy(screen = ClientProfileScreen.Main) }
            ClientProfileEvent.OnEditProfileClicked -> openEditProfile()
            ClientProfileEvent.OnMyReviewsClicked -> openMyReviews()
            ClientProfileEvent.OnContactUsClicked -> openContactUs()
            ClientProfileEvent.OnDeleteAccountClicked -> openDeleteAccount()
            is ClientProfileEvent.OnReviewClicked -> sendEffect(ClientProfileEffect.OpenVenueDetails(event.venueId))
            is ClientProfileEvent.OnEditFirstNameChanged -> updateState { it.copy(editFirstName = event.value) }
            is ClientProfileEvent.OnEditLastNameChanged -> updateState { it.copy(editLastName = event.value) }
            is ClientProfileEvent.OnEditEmailChanged -> updateState { it.copy(editEmail = event.value) }
            ClientProfileEvent.OnEditAvatarClicked -> sendEffect(ClientProfileEffect.OpenAvatarPicker)
            is ClientProfileEvent.OnEditAvatarPicked -> uploadAvatar(event.file)
            ClientProfileEvent.OnSaveProfileClicked -> saveProfile()
            is ClientProfileEvent.OnContactSubjectChanged -> updateState { it.copy(contactSubject = event.value) }
            is ClientProfileEvent.OnContactDescriptionChanged -> updateState { it.copy(contactDescription = event.value) }
            ClientProfileEvent.OnSubmitContactClicked -> submitContactMessage()
            is ClientProfileEvent.OnDeleteEmailChanged -> updateState { it.copy(deleteEmail = event.value) }
            is ClientProfileEvent.OnDeleteReasonChanged -> updateState { it.copy(deleteReason = event.value) }
            ClientProfileEvent.OnConfirmDeleteAccountClicked -> deleteAccount()
        }
    }

    private fun loadProfile() {
        viewModelScope.launch {
            if (!hasToken()) {
                updateState {
                    it.copy(
                        isLoadingProfile = false,
                        profile = null,
                        requiresAuth = true
                    )
                }
                return@launch
            }

            updateState { it.copy(isLoadingProfile = true, requiresAuth = false) }
            when (val result = getProfileUseCase.execute(Unit)) {
                is DomainResult.Success -> {
                    updateState {
                        it.copy(
                            isLoadingProfile = false,
                            profile = result.data,
                            requiresAuth = false
                        )
                    }
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isLoadingProfile = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_load_error)
                    )
                }
            }
        }
    }

    private fun openEditProfile() {
        val profile = currentState.profile ?: return
        updateState {
            it.copy(
                screen = ClientProfileScreen.EditProfile,
                editFirstName = profile.firstName,
                editLastName = profile.lastName,
                editPhone = profile.phone,
                editEmail = profile.email.orEmpty(),
                editAvatarUrl = profile.avatarUrl,
                editAvatarMediaId = profile.avatarMediaId
            )
        }
    }

    private fun openMyReviews() {
        updateState { it.copy(screen = ClientProfileScreen.MyReviews) }
        loadMyReviews()
    }

    private fun openContactUs() {
        updateState { it.copy(screen = ClientProfileScreen.ContactUs) }
    }

    private fun openDeleteAccount() {
        updateState {
            it.copy(
                screen = ClientProfileScreen.DeleteAccount,
                deleteEmail = it.profile?.email.orEmpty(),
                deleteReason = ""
            )
        }
    }

    private fun saveProfile() {
        if (currentState.isAvatarUploading || currentState.isSavingProfile) return

        val firstName = currentState.editFirstName.trim()
        val lastName = currentState.editLastName.trim()
        val phone = currentState.profile?.phone.orEmpty()
        val email = currentState.editEmail.trim().takeIf { it.isNotEmpty() }

        when {
            firstName.isBlank() -> {
                showError(UiText.StringResource(Res.string.client_profile_validation_first_name))
                return
            }
            lastName.isBlank() -> {
                showError(UiText.StringResource(Res.string.client_profile_validation_last_name))
                return
            }
        }

        viewModelScope.launch {
            updateState { it.copy(isSavingProfile = true) }
            val request = UpdateCurrentUserRequest(
                firstName = firstName,
                lastName = lastName,
                phone = phone,
                email = email,
                avatarUrl = currentState.editAvatarUrl,
                avatarMediaId = currentState.editAvatarMediaId
            )

            when (val result = updateProfileUseCase.execute(request)) {
                is DomainResult.Success -> {
                    updateState {
                        it.copy(
                            isSavingProfile = false,
                            profile = result.data,
                            editAvatarUrl = result.data.avatarUrl,
                            editAvatarMediaId = result.data.avatarMediaId,
                            screen = ClientProfileScreen.Main
                        )
                    }
                    showSuccess(UiText.StringResource(Res.string.client_profile_save_success))
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isSavingProfile = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_save_error)
                    )
                }
            }
        }
    }

    private fun uploadAvatar(file: ClientProfilePickedFile) {
        viewModelScope.launch {
            updateState { it.copy(isAvatarUploading = true) }
            when (
                val result = uploadImageUseCase.execute(
                    UploadImageCommand(
                        file = file.fileSource,
                        type = ImageType.AVATAR,
                        mimeType = file.mimeType
                    )
                )
            ) {
                is DomainResult.Success -> {
                    attachAvatarToProfile(
                        avatarUrl = result.data.publicUrl,
                        avatarMediaId = result.data.mediaId
                    )
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isAvatarUploading = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_avatar_upload_error)
                    )
                }
            }
        }
    }

    private suspend fun attachAvatarToProfile(
        avatarUrl: String,
        avatarMediaId: String
    ) {
        val profile = currentState.profile
        if (profile == null) {
            updateState {
                it.copy(
                    isAvatarUploading = false,
                    editAvatarUrl = avatarUrl,
                    editAvatarMediaId = avatarMediaId
                )
            }
            return
        }

        val request = UpdateCurrentUserRequest(
            firstName = profile.firstName,
            lastName = profile.lastName,
            phone = profile.phone,
            email = profile.email,
            avatarUrl = avatarUrl,
            avatarMediaId = avatarMediaId
        )

        when (val result = updateProfileUseCase.execute(request)) {
            is DomainResult.Success -> {
                updateState {
                    it.copy(
                        isAvatarUploading = false,
                        profile = result.data,
                        editAvatarUrl = result.data.avatarUrl,
                        editAvatarMediaId = result.data.avatarMediaId
                    )
                }
            }
            is DomainResult.Error -> {
                updateState {
                    it.copy(
                        isAvatarUploading = false,
                        editAvatarUrl = avatarUrl,
                        editAvatarMediaId = avatarMediaId
                    )
                }
                showError(
                    result.message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_profile_save_error)
                )
            }
        }
    }

    private fun loadMyReviews() {
        viewModelScope.launch {
            if (!hasToken()) {
                updateState { it.copy(isLoadingReviews = false, reviews = emptyList(), requiresAuth = true) }
                return@launch
            }

            updateState { it.copy(isLoadingReviews = true, requiresAuth = false) }
            when (
                val result = getMyReviewsUseCase.execute(
                    GetClientMyReviewsCommand(page = 1, pageSize = ReviewsPageSize)
                )
            ) {
                is DomainResult.Success -> {
                    updateState {
                        it.copy(
                            isLoadingReviews = false,
                            reviews = result.data.items
                        )
                    }
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isLoadingReviews = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_reviews_load_error)
                    )
                }
            }
        }
    }

    private fun submitContactMessage() {
        val subject = currentState.contactSubject.trim()
        val description = currentState.contactDescription.trim()

        when {
            subject.isBlank() -> {
                showError(UiText.StringResource(Res.string.client_profile_validation_contact_subject))
                return
            }
            description.isBlank() -> {
                showError(UiText.StringResource(Res.string.client_profile_validation_contact_description))
                return
            }
        }

        viewModelScope.launch {
            updateState { it.copy(isSendingContact = true) }
            when (
                val result = submitContactMessageUseCase.execute(
                    ContactSupportRequest(
                        subject = subject,
                        description = description
                    )
                )
            ) {
                is DomainResult.Success -> {
                    updateState {
                        it.copy(
                            isSendingContact = false,
                            contactSubject = "",
                            contactDescription = ""
                        )
                    }
                    showSuccess(UiText.StringResource(Res.string.client_profile_contact_success))
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isSendingContact = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_contact_error)
                    )
                }
            }
        }
    }

    private fun deleteAccount() {
        val expectedEmail = currentState.profile?.email?.trim().orEmpty()
        val email = currentState.deleteEmail.trim()
        val reason = currentState.deleteReason.trim()

        if (expectedEmail.isNotBlank() && email.isBlank()) {
            showError(UiText.StringResource(Res.string.client_profile_validation_delete_email))
            return
        }
        if (reason.isBlank()) {
            showError(UiText.StringResource(Res.string.client_profile_validation_delete_reason))
            return
        }

        viewModelScope.launch {
            updateState { it.copy(isDeletingAccount = true) }
            when (
                val result = deleteClientAccountUseCase.execute(
                    DeleteCurrentUserRequest(
                        email = email.takeIf { it.isNotBlank() },
                        reason = reason
                    )
                )
            ) {
                is DomainResult.Success -> {
                    tokenStorage.clear()
                    updateState { ClientProfileViewState(requiresAuth = true) }
                    showSuccess(UiText.StringResource(Res.string.client_profile_delete_success))
                    sendEffect(ClientProfileEffect.OpenAuth)
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isDeletingAccount = false) }
                    showError(
                        result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_profile_delete_error)
                    )
                }
            }
        }
    }

    private fun logout() {
        viewModelScope.launch {
            tokenStorage.clear()
            updateState { ClientProfileViewState(requiresAuth = true) }
            sendEffect(ClientProfileEffect.OpenAuth)
        }
    }

    private suspend fun hasToken(): Boolean {
        return !tokenStorage.getAccessToken().isNullOrBlank()
    }

    private fun showError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }

    private fun showSuccess(message: UiText) {
        alertController.show(
            title = UiText.StringResource(Res.string.client_profile_success_title),
            message = message,
            type = NotificationType.SUCCESS
        )
    }

    private companion object {
        const val ReviewsPageSize = 50
    }
}
